import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus, Wrench, Users } from 'lucide-react';

/**
 * Getting Started Section with toggle switch and 3 vertical cards
 * YouTube MMO focused content
 */
const GettingStartedSection: React.FC = () => {
    const { t } = useTranslation();
    const [selectedMode, setSelectedMode] = useState<'user' | 'creator'>('user');

    const steps = [
        {
            number: '01',
            title: t('landing.gettingStarted.register.title', 'Đăng ký & Tạo kênh'),
            description: t('landing.gettingStarted.register.desc', 'Đăng ký tài khoản và tạo kênh YouTube của bạn để bắt đầu hành trình trở thành YouTuber chuyên nghiệp'),
            icon: <UserPlus className="w-8 h-8" />,
            image: '/images/register-demo.png',
        },
        {
            number: '02',
            title: t('landing.gettingStarted.tools.title', 'Sử dụng AI Tools'),
            description: t('landing.gettingStarted.tools.desc', 'Khám phá bộ công cụ AI mạnh mẽ: Veo3 tạo video tự động, Whisk tạo ảnh nhân vật đồng nhất, và Voice Clone cho giọng nói chuyên nghiệp'),
            icon: <Wrench className="w-8 h-8" />,
            image: '/images/tools-demo.png',
        },
        {
            number: '03',
            title: t('landing.gettingStarted.community.title', 'Tham gia Cộng đồng'),
            description: t('landing.gettingStarted.community.desc', 'Kết nối với hàng nghìn YouTuber, học hỏi kinh nghiệm, chia sẻ tips và cùng nhau phát triển kênh YouTube thành công'),
            icon: <Users className="w-8 h-8" />,
            image: '/images/community-demo.png',
        },
    ];

    return (
        <section className="relative py-20 px-4 sm:px-6 lg:px-8">
            {/* Light gradient background - orange to blue/purple */}
            <div className="absolute inset-0 bg-gradient-to-r from-orange-50 via-white to-blue-50"></div>
            
            <div className="relative z-10 max-w-4xl mx-auto">
                {/* Title */}
                <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-black mb-8 text-center">
                    {t('landing.gettingStarted.title', 'Bắt đầu hành trình YouTube của bạn')}
                </h2>

                {/* Toggle Switch */}
                <div className="flex justify-center mb-12">
                    <div className="relative inline-flex items-center bg-white rounded-full p-1 border border-gray-200 shadow-lg">
                        {/* Background indicator */}
                        <div
                            className="absolute top-1 bottom-1 rounded-full bg-orange-500 transition-all duration-300 ease-out"
                            style={{
                                left: selectedMode === 'user' ? 4 : '50%',
                                right: selectedMode === 'user' ? '50%' : 4,
                            }}
                        />
                        
                        {/* User button */}
                        <button
                            onClick={() => setSelectedMode('user')}
                            className={`relative z-10 px-6 py-2 rounded-full font-medium text-sm transition-colors duration-200 ${
                                selectedMode === 'user'
                                    ? 'text-white'
                                    : 'text-gray-700'
                            }`}
                        >
                            {t('landing.gettingStarted.mode.user', 'Tôi là người mới')}
                        </button>
                        
                        {/* Creator button */}
                        <button
                            onClick={() => setSelectedMode('creator')}
                            className={`relative z-10 px-6 py-2 rounded-full font-medium text-sm transition-colors duration-200 ${
                                selectedMode === 'creator'
                                    ? 'text-white'
                                    : 'text-gray-700'
                            }`}
                        >
                            {t('landing.gettingStarted.mode.creator', 'Tôi là Creator')}
                        </button>
                    </div>
                </div>

                {/* Steps Cards - Vertical Stack */}
                <div className="space-y-6">
                    {steps.map((step, index) => (
                        <div
                            key={index}
                            className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
                        >
                            <div className="flex flex-col lg:flex-row items-start gap-6">
                                {/* Left: Number, Icon, and divider line */}
                                <div className="flex items-center gap-4 flex-shrink-0 w-full lg:w-auto">
                                    <div className="text-4xl sm:text-5xl font-bold text-gray-300">
                                        {step.number}
                                    </div>
                                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white shadow-lg relative overflow-hidden group hover:scale-110 hover:rotate-3 transition-transform">
                                        {/* Shimmer effect */}
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer-sweep" />
                                        <div className="relative z-10">
                                            {step.icon}
                                        </div>
                                    </div>
                                    <div className="hidden lg:block w-px h-12 bg-gray-200"></div>
                                </div>

                                {/* Middle: Content */}
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-black mb-3">
                                        {step.title}
                                    </h3>
                                    <p className="text-gray-600 text-sm sm:text-base lg:text-lg leading-relaxed">
                                        {step.description}
                                    </p>
                                </div>

                                {/* Right: Image/Visual */}
                                <div className="w-full lg:w-48 h-32 sm:h-40 lg:h-40 rounded-lg bg-gradient-to-br from-orange-100 via-orange-50 to-blue-100 flex items-center justify-center flex-shrink-0 overflow-hidden relative group hover:scale-105 transition-transform">
                                    {/* Glow effect on hover */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-orange-400/20 to-blue-400/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" />
                                    
                                    {/* Placeholder for screenshot/image */}
                                    {step.image ? (
                                        <img
                                            src={step.image}
                                            alt={step.title}
                                            className="w-full h-full object-cover rounded-lg relative z-10 group-hover:scale-110 group-hover:brightness-110 transition-all"
                                            onError={(e) => {
                                                // Fallback if image doesn't exist
                                                e.currentTarget.style.display = 'none';
                                            }}
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-orange-100 to-blue-100 rounded-lg relative z-10" />
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default GettingStartedSection;

